import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_USER_DETAILS_XLS } from '../../../../graphql/Query/users';
import * as XLSX from 'xlsx';

const generateSingleRowArray = (userData, maxActivitiesPerRoad = []) => {
  const {
    _id,
    fullName,
    email,
    phonecode,
    membership,
    createdAt,
    gender,
    country,
    phone,
    status,
    referralCode,
    usedReferralCode,
    timezone,
    expoToken,
    platform,
    disable,
    fullRecord,
    onboardingStep,
    expiredFreemium,
    streakDate,
    claimcredit,
    membershipPeriod,
    membershipExpired,
    pillars,
    emotions,
    roadshistory
  } = userData;

  const profileData = [
    _id,
    fullName,
    email,
    phone,
    phonecode,
    country,
    gender,
    new Date(createdAt).toLocaleString(),
    fullRecord ? 'Sí' : 'No',
    onboardingStep || '',
    referralCode || 'no tiene',
    usedReferralCode || 'no tiene',
    membership,
    expiredFreemium ? new Date(expiredFreemium).toLocaleString() : '',
    status,
    timezone || '',
    expoToken || '',
    platform || '',
    disable ? 'Sí' : 'No',
    streakDate ? new Date(streakDate).toLocaleString() : '',
    claimcredit || '',
    membershipPeriod || '',
    membershipExpired ? new Date(membershipExpired).toLocaleString() : '',
    pillars[0]?.emotions || 0,
    pillars[0]?.relations || 0,
    pillars[0]?.transformation || 0,
    pillars[0]?.purpose || 0,
    pillars[0]?.selfEsteem || 0
  ];

  const emotionTitles = [
    'alegria',
    'ansiedad',
    'confianza',
    'enfado',
    'incomodidad',
    'miedo',
    'sorpresa',
    'tristeza'
  ];
  const consumptionData = emotionTitles.map((title) => {
    const record = emotions?.find((e) => e.tag === title);
    return record
      ? record.total > 0
        ? ((record.points * 100) / record.total).toFixed(0)
        : 0
      : 0;
  });

  const pathsData = [];
  const maxRoads = maxActivitiesPerRoad.length;
  
  // Iterar sobre el número máximo de caminos para asegurar que todas las filas tengan la misma estructura
  for (let index = 0; index < maxRoads; index++) {
    const road = roadshistory?.[index];
    const maxActivities = maxActivitiesPerRoad[index] || 0;
    
    if (road) {
      // Campos base del camino
      pathsData.push(road.name || '');
      pathsData.push(road.completed ? 'Sí' : 'No');
      pathsData.push(road.episodes || 0);
      pathsData.push(road.episodesCompleted || 0);
      const completionPercentage = road.episodes > 0
        ? ((road.episodesCompleted / road.episodes) * 100).toFixed(0)
        : '0';
      pathsData.push(`${completionPercentage}%`);
      pathsData.push(road.activityhistory?.length || 0);
      
      // Agregar información de cada actividad en el historial
      const activities = road.activityhistory || [];
      for (let j = 0; j < maxActivities; j++) {
        const activity = activities[j];
        if (activity) {
          pathsData.push(activity.episode || '');
          pathsData.push(activity.activity || '');
          
          // Formatear preguntas y respuestas del leadForm
          let leadFormText = '';
          if (activity.leadForm) {
            const leadFormArray = Array.isArray(activity.leadForm) ? activity.leadForm : [activity.leadForm];
            const formattedQuestions = leadFormArray
              .filter(formItem => formItem && formItem.form && formItem.form.length > 0)
              .map(formItem => {
                const question = formItem.form[0]?.question || '';
                const response = formItem.response || '';
                return `["${question}": "${response}"]`;
              });
            leadFormText = formattedQuestions.join(',\n');
          }
          pathsData.push(leadFormText || 'No');
        } else {
          // Si no hay actividad en esta posición, agregar campos vacíos
          pathsData.push('');
          pathsData.push('');
          pathsData.push('');
        }
      }
    } else {
      // Si no hay camino en esta posición, agregar campos vacíos
      pathsData.push(''); // nombre
      pathsData.push(''); // completado
      pathsData.push(''); // episodios
      pathsData.push(''); // episodios completados
      pathsData.push(''); // porcentaje
      pathsData.push(''); // número de actividades
      
      // Agregar campos vacíos para todas las actividades de este camino
      for (let j = 0; j < maxActivities; j++) {
        pathsData.push(''); // episodio
        pathsData.push(''); // actividad
        pathsData.push(''); // tiene leadForm
      }
    }
  }
  const singleRowArray = [...profileData, ...consumptionData, ...pathsData];

  return singleRowArray;
};

const convertArrayOfObjectsToXLSX = (array) => {
  if (!array || !array.length) return null;
  
  try {
    // Calcular el número máximo de caminos para determinar cuántas columnas necesitamos
    // Solo usar el número real de caminos que existen en los datos (validando que sean arrays válidos)
    // Limitar a un máximo razonable para evitar errores de memoria
    const MAX_ROADS_LIMIT = 20; // Límite máximo de caminos a procesar
    const roadLengths = array
      .map(item => {
        const roads = item.roadshistory;
        // Validar que roadshistory sea un array válido y no vacío
        if (Array.isArray(roads) && roads.length > 0) {
          return roads.length;
        }
        return 0;
      })
      .filter(length => length > 0 && isFinite(length));
    const rawMaxRoads = roadLengths.length > 0 ? Math.max(...roadLengths) : 0;
    const maxRoads = Math.min(Math.max(0, Math.floor(rawMaxRoads || 0)), MAX_ROADS_LIMIT); // Limitar y validar el número de caminos
    
    // Validar que maxRoads sea un número válido
    if (!isFinite(maxRoads) || maxRoads < 0) {
      console.error('Error: maxRoads no es un número válido:', maxRoads);
      return null;
    }
    
    // Calcular el número máximo de actividades por camino
    // Solo calcular para los caminos que realmente existen
    const MAX_ACTIVITIES_PER_ROAD = 100; // Límite máximo de actividades por camino
    const maxActivitiesPerRoad = [];
    for (let i = 0; i < maxRoads; i++) {
      const activityLengths = array
        .map(item => {
          const road = item.roadshistory?.[i];
          return road?.activityhistory?.length || 0;
        })
        .filter(length => length > 0);
      const rawMaxActivities = activityLengths.length > 0 ? Math.max(...activityLengths) : 0;
      const maxActivities = Math.min(rawMaxActivities, MAX_ACTIVITIES_PER_ROAD);
      maxActivitiesPerRoad.push(maxActivities);
    }
    
    // Construir title con campos base
    const title = [
    'ID aplicación',
    'Nombre Completo',
    'Correo Electrónico',
    'Teléfono',
    'Código Telefónico',
    'País',
    'Género',
    'Fecha de Registro',
    'Registro Completo',
    'Paso de Onboarding',
    'Código de Referencia',
    'Código de Referencia Usado',
    'Membresía',
    'Freemium Expirado',
    'Estado',
    'Zona Horaria',
    'Token Expo',
    'Plataforma',
    'Deshabilitado',
    'Fecha de Racha',
    'Crédito Reclamado',
    'Período de Membresía',
    'Membresía Expirada',
    'Emociones',
    'Relaciones',
    'Transformacion',
    'Sentido de la vida',
    'Amor Propio',
    'alegria',
    'ansiedad',
    'confianza',
    'enfado',
    'incomodidad',
    'miedo',
      'sorpresa',
      'tristeza'
    ];
    
    // Agregar títulos dinámicos para los caminos y sus actividades
    for (let i = 0; i < maxRoads; i++) {
      const maxActivities = maxActivitiesPerRoad[i];
      // Campos base del camino
      title.push(
        `Camino ${i + 1} - Nombre`,
        `Camino ${i + 1} - Completado`,
        `Camino ${i + 1} - Episodios`,
        `Camino ${i + 1} - Episodios Completados`,
        `Camino ${i + 1} - Porcentaje`,
        `Camino ${i + 1} - Número de Actividades`
      );
      
      // Campos para cada actividad
      for (let j = 0; j < maxActivities; j++) {
        title.push(
          `Camino ${i + 1} - Actividad ${j + 1} - Episodio`,
          `Camino ${i + 1} - Actividad ${j + 1} - Nombre`,
          `Camino ${i + 1} - Actividad ${j + 1} - LeadForm (Preguntas y Respuestas)`
        );
      }
    }
    
    // Construir constTitle dinámicamente después de construir title
    // El constTitle debe tener la misma longitud que title
    // Perfil: 23 campos, Pilares: 5 campos, Emociones: 8 campos, Caminos: variable
    const totalColumns = title.length;
    const profileColumns = 23; // Campos de perfil
    const pillarColumns = 5; // Campos de pilares
    const emotionColumns = 8; // Campos de emociones
    const roadColumns = Math.max(0, totalColumns - profileColumns - pillarColumns - emotionColumns);
    
    // Validar que roadColumns sea un número válido y no exceda límites
    // Limitar a un máximo razonable para evitar errores de memoria
    const MAX_ROAD_COLUMNS = 5000; // Límite conservador
    const validRoadColumns = Math.min(Math.max(0, Math.floor(roadColumns)), MAX_ROAD_COLUMNS);
    
    // Función auxiliar para crear arrays de forma segura
    const safeArray = (length) => {
      try {
        // Validar que length sea un número finito y válido
        if (!isFinite(length) || length === null || length === undefined) {
          return [];
        }
        const safeLength = Math.max(0, Math.min(Math.floor(length), 10000));
        if (safeLength <= 0 || !isFinite(safeLength)) {
          return [];
        }
        return Array(safeLength).fill('');
      } catch (error) {
        console.error('Error al crear array:', error, 'length:', length);
        return [];
      }
    };
    
    const constTitle = [
      'Perfil del Usuario',
      ...safeArray(profileColumns - 1), // 22 campos de perfil adicionales (23 total - 1 título)
      'Puntos por Pilar',
      ...safeArray(pillarColumns - 1), // 4 campos de pilares adicionales (5 total - 1 título)
      'Consumo por Emociones',
      ...safeArray(emotionColumns - 1), // 7 emociones adicionales (8 total - 1 título)
      'Historial de Caminos',
      ...safeArray(validRoadColumns - 1)
    ];
    
    // Asegurar que constTitle tenga la misma longitud que title
    while (constTitle.length < title.length) {
      constTitle.push('');
    }
    while (constTitle.length > title.length) {
      constTitle.pop();
    }
    
    const data = [constTitle, title];

    array.forEach((item) => {
      const row = generateSingleRowArray(item, maxActivitiesPerRoad);
      // Asegurar que todas las filas tengan la misma longitud
      while (row.length < title.length) {
        row.push('');
      }
      data.push(row);
    });

    return data;
  } catch (error) {
    console.error('Error en convertArrayOfObjectsToXLSX:', error);
    return null;
  }
};

// Función para calcular el ancho de las columnas basado en el contenido
const calculateColumnWidths = (data) => {
  if (!data || !data.length) return [];
  
  const maxWidths = [];
  const maxRowsToCheck = Math.min(data.length, 100); // Limitar a las primeras 100 filas para rendimiento
  
  // Inicializar con el ancho de los títulos
  if (data[0] && Array.isArray(data[0])) {
    data[0].forEach((cell, colIndex) => {
      const cellValue = cell ? String(cell) : '';
      maxWidths[colIndex] = cellValue.length;
    });
  }
  
  // Revisar el contenido de las filas de datos
  for (let rowIndex = 1; rowIndex < maxRowsToCheck; rowIndex++) {
    const row = data[rowIndex];
    if (Array.isArray(row)) {
      row.forEach((cell, colIndex) => {
        const cellValue = cell ? String(cell) : '';
        const cellLength = cellValue.length;
        if (!maxWidths[colIndex] || cellLength > maxWidths[colIndex]) {
          maxWidths[colIndex] = cellLength;
        }
      });
    }
  }
  
  // Convertir a anchos de columna (en unidades de Excel)
  // Excel usa aproximadamente 1 unidad = 1 carácter para fuentes estándar
  // Agregamos un pequeño margen para mejor legibilidad
  return maxWidths.map((width) => ({
    wch: Math.min(Math.max(width + 2, 10), 50) // Mínimo 10, máximo 50 caracteres
  }));
};

const downloadCSV = (array) => {
  const anchor = document.createElement('a');
  const data = convertArrayOfObjectsToXLSX(array);
  if (data === null) return;

  const filename = 'subliful_all_User.xlsx';

  const workbook = XLSX.utils.book_new(),
    worksheet = XLSX.utils.aoa_to_sheet(data);
  
  // Calcular y aplicar anchos de columna automáticos
  const columnWidths = calculateColumnWidths(data);
  if (columnWidths.length > 0) {
    worksheet['!cols'] = columnWidths;
  }
  
  workbook.SheetNames.push('First');
  workbook.Sheets['First'] = worksheet;
  const xlsbin = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'binary'
  });

  const buffer = new ArrayBuffer(xlsbin.length),
    array2 = new Uint8Array(buffer);
  for (let i = 0; i < xlsbin.length; i++) {
    array2[i] = xlsbin.charCodeAt(i) & 0xff;
  }
  const xlsblob = new Blob([buffer], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(xlsblob);

  anchor.href = url;
  anchor.download = filename;
  anchor.click();
};

export const useDownloadCSV = () => {
  const [fetchUserAll, { loading: loadingDownload, data: allData }] =
    useLazyQuery(GET_USER_DETAILS_XLS, {
      variables: {
        input: {}
      },
      fetchPolicy: 'no-cache'
    });

  useEffect(() => {
    if (allData) {
      console.log(allData);
      downloadCSV(allData?.excelUserList);
    }
  }, [allData]);

  const handleDownload = async () => {
    fetchUserAll();
  };

  return {
    handleDownload,
    loadingDownload
  };
};

